import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  generateOwnerReceipt,
  generateGSTInvoice,
  generateBookingReport,
} from '../services/pdfGenerator';

const OWNER_SAMPLE = {
  useDbAmounts: true,
  receiptNo: 'DE/RCPT/OWN/2026/041001',
  bookingId: 'BK-2026-041001',
  settlementId: 'a1b2c3d4e5f67890',
  payDate: new Date().toLocaleDateString('en-IN'),
  period: 'Apr 2026',
  ownerName: 'Rajesh Patil',
  grossAmount: 2756,
  commissionAmount: 413,
  tdsAmount: 55,
  gstTcsAmount: 28,
  netAmount: 2333,
  netOwnerAmount: 2333,
  commissionPct: 15,
  hours: 2,
  ratePerHour: 1400,
  words: 'Rupees Two Thousand Three Hundred Thirty Three Only',
  paymentMode: 'Wallet Credit',
  paymentRef: 'WALCRED2026041001',
  walletDestination: 'Development Express Owner Wallet',
  status: 'settled',
};

const CLIENT_INVOICE_SAMPLE = {
  invoiceNo: 'DE/INV/2026/041001',
  invoiceDate: new Date().toLocaleDateString('en-IN'),
  bookingId: 'BK-2026-041001',
  clientName: 'Patil Builders Pvt. Ltd.',
  clientAddr: 'Karad, Satara — 415110',
  clientGST: '27AABCP1234A1Z5',
  clientPhone: '+91-9876543210',
  clientEmail: 'billing@patilbuilders.com',
  machineName: 'JCB 3DX Backhoe Loader',
  operator: 'Ramesh Kadam',
  location: 'Karad, Satara',
  workPeriod: '01-10 Apr 2026',
  hours: 75,
  ratePerHour: 1400,
  baseAmount: 105000,
  advancePaid: 42000,
};

const BOOKING_ROWS = [
  {
    id: 'BK-2026-041001',
    date: '01 Apr 2026',
    machine: 'JCB 3DX',
    type: 'Daily',
    hours: 75,
    baseAmt: 105000,
    gst: 18900,
    total: 123900,
    status: 'Completed',
  },
  {
    id: 'BK-2026-041002',
    date: '12 Apr 2026',
    machine: 'Poclain 220',
    type: 'Weekly',
    hours: 40,
    baseAmt: 56000,
    gst: 10080,
    total: 66080,
    status: 'Active',
  },
];

const PDF_TYPES = [
  { id: 'owner', label: 'Owner Receipt', hint: '2-page settlement receipt' },
  { id: 'invoice', label: 'Client GST Invoice', hint: 'Tax invoice per booking' },
  { id: 'report', label: 'Client Booking Report', hint: 'Reports tab — booking history' },
];

async function buildPreview(generator) {
  const doc = await generator({ download: false });
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  return { url, filename: doc.__deFilename || 'preview.pdf' };
}

export default function PdfPreviewPage() {
  const [active, setActive] = useState('report');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [filename, setFilename] = useState('');
  const previewUrlRef = useRef('');

  const revokeUrl = useCallback((url) => {
    if (url) URL.revokeObjectURL(url);
  }, []);

  const runPreview = useCallback(async (typeId) => {
    setLoading(true);
    setError('');
    revokeUrl(previewUrlRef.current);
    previewUrlRef.current = '';
    setPreviewUrl('');
    try {
      let result;
      if (typeId === 'owner') {
        result = await buildPreview((opts) => generateOwnerReceipt(OWNER_SAMPLE, opts));
      } else if (typeId === 'invoice') {
        result = await buildPreview((opts) => generateGSTInvoice(CLIENT_INVOICE_SAMPLE, opts));
      } else {
        result = await buildPreview((opts) =>
          generateBookingReport(BOOKING_ROWS, 'Patil Builders Pvt. Ltd.', opts),
        );
      }
      previewUrlRef.current = result.url;
      setPreviewUrl(result.url);
      setFilename(result.filename);
    } catch (err) {
      setError(err?.message || 'Could not generate PDF preview');
    } finally {
      setLoading(false);
    }
  }, [revokeUrl]);

  useEffect(() => {
    runPreview(active);
    return () => revokeUrl(previewUrlRef.current);
  }, [active, runPreview, revokeUrl]);

  if (process.env.NODE_ENV === 'production') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#050d1a', color: '#8896a8' }}>
        <p>PDF preview is only available on localhost (development).</p>
      </div>
    );
  }

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = filename;
    a.click();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #050d1a, #0a1628)',
        color: '#e8e0d0',
        fontFamily: 'Arial, sans-serif',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(201,168,76,0.25)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <p style={{ color: '#c9a84c', fontWeight: 700, fontSize: 12, margin: '0 0 4px' }}>DEV — LOCALHOST PDF PREVIEW</p>
          <h1 style={{ color: '#fff', fontSize: 20, margin: 0 }}>Premium PDF Preview</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!previewUrl || loading}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderRadius: 8,
              cursor: previewUrl ? 'pointer' : 'not-allowed',
              background: 'linear-gradient(135deg, #a07830, #e2c97e)',
              color: '#0a1628',
              fontWeight: 700,
              fontSize: 13,
              opacity: previewUrl ? 1 : 0.5,
            }}
          >
            Download
          </button>
          <button
            type="button"
            onClick={() => runPreview(active)}
            disabled={loading}
            style={{
              padding: '10px 16px',
              border: '1px solid rgba(201,168,76,0.4)',
              borderRadius: 8,
              background: 'transparent',
              color: '#c9a84c',
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? 'Generating…' : 'Refresh'}
          </button>
        </div>
      </header>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 20px', borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
        {PDF_TYPES.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActive(t.id)}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: active === t.id ? '1px solid #c9a84c' : '1px solid rgba(201,168,76,0.2)',
              background: active === t.id ? 'rgba(201,168,76,0.15)' : 'rgba(0,0,0,0.2)',
              color: active === t.id ? '#c9a84c' : '#8896a8',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span style={{ display: 'block', fontWeight: 700, fontSize: 13 }}>{t.label}</span>
            <span style={{ display: 'block', fontSize: 10, marginTop: 2 }}>{t.hint}</span>
          </button>
        ))}
      </div>

      {error && (
        <p style={{ color: '#e94560', padding: '12px 20px', margin: 0, fontSize: 13 }}>{error}</p>
      )}

      <div style={{ flex: 1, minHeight: 0, padding: 16, display: 'flex', flexDirection: 'column' }}>
        {loading && !previewUrl && (
          <p style={{ color: '#8896a8', textAlign: 'center', marginTop: 40 }}>Building PDF preview…</p>
        )}
        {previewUrl && (
          <iframe
            title="PDF preview"
            src={previewUrl}
            style={{
              flex: 1,
              width: '100%',
              minHeight: '70vh',
              border: '1px solid rgba(201,168,76,0.3)',
              borderRadius: 12,
              background: '#fff',
            }}
          />
        )}
      </div>

      <footer style={{ padding: '12px 20px', borderTop: '1px solid rgba(201,168,76,0.15)', fontSize: 11, color: '#8896a8' }}>
        Open: <code style={{ color: '#c9a84c' }}>http://localhost:3000/dev/pdf</code>
        {' · '}
        <Link to="/client" style={{ color: '#c9a84c' }}>Client</Link>
        {' · '}
        <Link to="/owner" style={{ color: '#c9a84c' }}>Owner</Link>
      </footer>
    </div>
  );
}
